import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/features/auth/store';
import { useShellI18n } from '@/features/language/useI18n';

const LOGIN_RETURN_PATH_KEY = 'shell-login-return-path';

const toReturnPath = (from: unknown) => {
  if (!from || typeof from !== 'object') {
    return null;
  }

  const location = from as { pathname?: unknown; search?: unknown; hash?: unknown };
  const pathname = typeof location.pathname === 'string' ? location.pathname : null;
  if (!pathname) {
    return null;
  }

  return `${pathname}${typeof location.search === 'string' ? location.search : ''}${typeof location.hash === 'string' ? location.hash : ''}`;
};

const getStoredReturnPath = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  return window.sessionStorage.getItem(LOGIN_RETURN_PATH_KEY);
};

/**
 * Login page using the same visual language as Community.
 */
export const LoginPage: React.FC = () => {
  const login = useAuthStore((state) => state.login);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const isLoading = useAuthStore((state) => state.isLoading);
  const error = useAuthStore((state) => state.error);
  const { t } = useShellI18n();
  const [requestEmail, setRequestEmail] = useState('');
  const [requestNotice, setRequestNotice] = useState<string | null>(null);
  const [requestError, setRequestError] = useState<string | null>(null);
  const [requestPending, setRequestPending] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const isRegisterView = location.pathname === '/register';
  const from = toReturnPath(location.state?.from) ?? getStoredReturnPath() ?? '/home';

  useEffect(() => {
    if (isAuthenticated) {
      window.sessionStorage.removeItem(LOGIN_RETURN_PATH_KEY);
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, from]);

  const openLoginView = () => {
    navigate('/login', {
      replace: true,
      state: location.state?.from ? { from: location.state.from } : null,
    });
  };

  const openRegisterView = () => {
    navigate('/register', {
      state: location.state?.from ? { from: location.state.from } : null,
    });
  };

  const handleAccessRequest = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!requestEmail.trim()) {
      return;
    }

    try {
      setRequestPending(true);
      setRequestError(null);
      const response = await fetch('/api/support/access/request', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: requestEmail.trim() }),
      });

      if (!response.ok) {
        throw new Error(`Request failed (${response.status})`);
      }

      const payload = (await response.json()) as { message?: string };
      setRequestNotice(
        payload.message ??
          'If your email is eligible for support access, you will receive a sign-in email shortly.'
      );
      setRequestEmail('');
    } catch (err) {
      setRequestError(err instanceof Error ? err.message : 'Request failed');
    } finally {
      setRequestPending(false);
    }
  };

  if (isLoading) {
    return (
      <div className="loading-screen">
        <div className="loading-card">
          <div className="shell-spinner" />
          <h1>{t('Starting secure login')}</h1>
        </div>
      </div>
    );
  }

  return (
    <section className="auth-shell">
      <div className="auth-card">
        {/*
          Das vertikale Lockup traegt die Wortmarke bereits, deshalb steht daneben
          keine zweite Ueberschrift. Die Seite bekommt ihren Titel aus dem
          Abschnitt darunter ("Anmelden" bzw. "Registrieren").

          Zwei Dateien statt einer: Das Marketing gibt Light fuer dunklen und Dark
          fuer hellen Grund vor. Welche gilt, entscheidet das CSS am data-theme,
          damit die Umschaltung ohne JavaScript und ohne Flackern passiert. Als
          Hintergrundbild und nicht als zwei img, weil sonst beide Dateien geladen
          und der Name des Logos zweimal vorgelesen wuerde.
        */}
        <div className="auth-brand">
          <span className="auth-lockup" role="img" aria-label="WIANCO" />
        </div>

        {error && (
          <div className="auth-error">
            <strong>{t('Authentication Error:')}</strong> {error.message}
          </div>
        )}

        {isRegisterView ? (
          <>
            <div className="auth-copy">
              <button className="auth-back" type="button" onClick={openLoginView}>
                {t('Back to login')}
              </button>
              <h1 className="auth-heading">{t('Register')}</h1>
              <p className="meta">
                {t('Enter your work email and we will send you a sign-in link.')}
              </p>
            </div>

            <form className="auth-actions" onSubmit={handleAccessRequest}>
              <div className="field">
                <label htmlFor="support-access-email">{t('Work email')}</label>
                <input
                  id="support-access-email"
                  type="email"
                  value={requestEmail}
                  onChange={(event) => setRequestEmail(event.target.value)}
                  placeholder={t('Work email')}
                />
              </div>
              <button
                className="btn"
                type="submit"
                disabled={requestPending || requestEmail.trim().length === 0}
              >
                {t('Send sign-in email')}
              </button>
            </form>

            {requestNotice && (
              <p className="auth-feedback auth-feedback-success">{t(requestNotice)}</p>
            )}
            {requestError && <p className="auth-feedback auth-feedback-error">{requestError}</p>}
          </>
        ) : (
          <>
            <div className="auth-copy">
              {/*
                Keine Unterzeile. Der Knopf darunter sagt bereits, was zu tun ist,
                und der Weg zur Registrierung steht als eigener Abschnitt darunter.
              */}
              <h1 className="auth-heading">{t('Portal sign-in')}</h1>
            </div>

            <div className="auth-actions">
              <button
                onClick={() => {
                  window.sessionStorage.setItem(LOGIN_RETURN_PATH_KEY, from);
                  void login();
                }}
                className="btn"
                type="button"
              >
                {t('Login')}
              </button>
            </div>

            <div className="auth-secondary">
              <span className="meta">{t('No account yet?')}</span>
              <button className="btn ghost" type="button" onClick={openRegisterView}>
                {t('Register')}
              </button>
            </div>
          </>
        )}

        <nav className="auth-legal-links" aria-label={t('Legal information')}>
          <Link to="/privacy-policy">{t('Privacy Policy')}</Link>
          <span aria-hidden="true">|</span>
          <Link to="/cookie-policy">{t('Cookie Policy')}</Link>
        </nav>
      </div>
    </section>
  );
};

export default LoginPage;
